// Domínio próprio para as landing pages (ex.: promo.seusite.com.br): cadastro, registros DNS e verificação.
import { useState } from 'react'
import { CheckCircle2, Clock, Copy, ExternalLink, Globe, RefreshCw, Trash2, XCircle } from 'lucide-react'
import type { LandingDomainDto, LandingPageSummaryDto } from '@impulso/shared/api/tracking'
import { errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { Empty, ErrorState, Loading } from '../../ui'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { useDomainAction, useLandingDomains, useLandingPages } from '../tracking/api'
import { fmtDateTime, LandingAddonCard } from '../tracking/components'

const STATUS = {
  pending: { cls: 'amber', text: 'Aguardando o DNS', Icon: Clock },
  active: { cls: 'green', text: 'No ar', Icon: CheckCircle2 },
  failed: { cls: 'red', text: 'Não verificado', Icon: XCircle },
} as const

export default function DomainsTab() {
  const company = useCompany()
  const canPublish = useCan('company.publish')
  const domains = useLandingDomains(company.id)
  const pages = useLandingPages(company.id)
  const act = useDomainAction(company.id)
  const toast = useToast()
  const [hostname, setHostname] = useState('')
  const [home, setHome] = useState('')

  if (domains.isLoading || pages.isLoading) return <Loading lines={4} />
  if (domains.error) return <ErrorState error={domains.error} onRetry={() => void domains.refetch()} />
  const { items, max } = domains.data!
  const allowance = pages.data?.allowance
  const published = (pages.data?.items ?? []).filter((p) => p.status === 'published')

  const add = (e: React.FormEvent) => {
    e.preventDefault()
    act.mutate({ kind: 'create', hostname, homePageId: home || null }, {
      onSuccess: () => { setHostname(''); toast('Domínio cadastrado. Agora crie os registros DNS abaixo.') },
      onError: (err) => toast(errorMessage(err)),
    })
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="callout">
        <Globe size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
        <span className="small">Use um endereço seu, como <b>promo.seusite.com.br</b>, em vez do endereço do Seu Impulso. A página principal aparece na raiz do domínio e as outras páginas publicadas em <b>promo.seusite.com.br/nome-da-pagina</b>. O rastreamento continua funcionando igual.</span>
      </div>
      {allowance && !allowance.active && <LandingAddonCard allowance={allowance} />}

      {canPublish && max > 0 && items.length < max && (
        <form className="card stack" style={{ gap: 12 }} onSubmit={add}>
          <h3>Adicionar domínio</h3>
          <div className="grid g-2" style={{ gap: 12 }}>
            <div className="field">
              <label htmlFor="dom-host">Endereço</label>
              <input id="dom-host" className="input" placeholder="promo.seusite.com.br" value={hostname} onChange={(e) => setHostname(e.target.value)} inputMode="url" />
              <span className="small muted">Prefira um subdomínio (com algo antes do nome do site). O seu site atual continua igual.</span>
            </div>
            <div className="field">
              <label htmlFor="dom-home">Página principal</label>
              <select id="dom-home" className="input" value={home} onChange={(e) => setHome(e.target.value)}>
                <option value="">Escolher depois</option>
                {published.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
          </div>
          <div><button className="btn primary" disabled={act.isPending || hostname.trim().length < 4}>Adicionar domínio</button></div>
        </form>
      )}

      {items.length === 0
        ? <Empty title="Nenhum domínio próprio ainda">Suas páginas funcionam normalmente no endereço do Seu Impulso. O domínio próprio é opcional.</Empty>
        : items.map((d) => <DomainCard key={d.id} domain={d} pages={published} />)}
      {max > 0 && <p className="small muted">{items.length} de {max} domínios usados.</p>}
    </div>
  )
}

function CopyCell({ value }: { value: string }) {
  const toast = useToast()
  return (
    <span className="row" style={{ gap: 4, minWidth: 0 }}>
      <code className="small" style={{ wordBreak: 'break-all' }}>{value}</code>
      <button type="button" className="mini-btn" aria-label={`Copiar ${value}`} onClick={() => navigator.clipboard.writeText(value).then(() => toast('Copiado!'), () => toast(value))}><Copy size={13} /></button>
    </span>
  )
}

function DomainCard({ domain: d, pages }: { domain: LandingDomainDto; pages: LandingPageSummaryDto[] }) {
  const company = useCompany()
  const canPublish = useCan('company.publish')
  const act = useDomainAction(company.id)
  const toast = useToast()
  const confirm = useConfirm()
  const st = STATUS[d.status]
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 10, minWidth: 0 }}>
          <h3 style={{ wordBreak: 'break-all' }}>{d.hostname}</h3>
          <span className={`badge ${st.cls}`}><st.Icon size={12} /> {st.text}</span>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {d.status === 'active' && <a className="btn sm" href={d.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Abrir</a>}
          {canPublish && d.status !== 'active' && (
            <button className="btn sm primary" disabled={act.isPending} onClick={() => act.mutate({ kind: 'verify', id: d.id }, {
              onSuccess: (r) => toast(r?.status === 'active' ? 'Domínio verificado e no ar!' : 'Ainda não deu. Confira os registros abaixo.'),
              onError: (e) => toast(errorMessage(e)),
            })}><RefreshCw size={14} className={act.isPending ? 'spin' : ''} /> Verificar agora</button>
          )}
          {canPublish && <button className="btn ghost sm" aria-label="Remover domínio" disabled={act.isPending} onClick={async () => { if (await confirm({ title: `Remover ${d.hostname}?`, message: 'As páginas voltam a ficar só no endereço do Seu Impulso.', confirmLabel: 'Remover', danger: true })) act.mutate({ kind: 'delete', id: d.id }, { onError: (e) => toast(errorMessage(e)) }) }}><Trash2 size={14} /></button>}
        </div>
      </div>

      {d.status !== 'active' && (
        <>
          <p className="small">No painel onde você comprou o domínio (Registro.br, Hostinger, GoDaddy, Cloudflare…), crie estes dois registros DNS:</p>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Tipo</th><th>Nome</th><th>Valor</th></tr></thead>
              <tbody>
                <tr><td className="strong">CNAME</td><td><CopyCell value={d.dns.cname.name} /></td><td><CopyCell value={d.dns.cname.value} /></td></tr>
                <tr><td className="strong">TXT</td><td><CopyCell value={d.dns.txt.name} /></td><td><CopyCell value={d.dns.txt.value} /></td></tr>
              </tbody>
            </table>
          </div>
          <p className="small muted">Se o painel já completa o nome com o seu domínio, digite só a parte que vem antes dele (por exemplo, "promo" e "_impulso.promo"). O CNAME é o registro que aponta o seu endereço para o nosso. No Cloudflare, deixe-o como "somente DNS" (nuvem cinza). A verificação é automática e pode levar algumas horas.</p>
          {d.lastError && <div className="form-error small">{d.lastError}{d.lastCheckAt ? ` (verificado em ${fmtDateTime(d.lastCheckAt)})` : ''}</div>}
        </>
      )}

      <div className="field" style={{ maxWidth: 420 }}>
        <label htmlFor={`home-${d.id}`}>Página principal (raiz do domínio)</label>
        <select id={`home-${d.id}`} className="input" value={d.homePageId ?? ''} disabled={!canPublish || act.isPending}
          onChange={(e) => act.mutate({ kind: 'home', id: d.id, homePageId: e.target.value || null }, { onSuccess: () => toast('Página principal atualizada.'), onError: (err) => toast(errorMessage(err)) })}>
          <option value="">Nenhuma</option>
          {pages.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>
    </div>
  )
}
